import useDevice from "@hooks/useDevice";
import NeuronProvider from "@web2/providers/NeuronProvider";
import type { ReactElement, ReactNode } from "react";
import { StrictMode, useEffect } from "react";
import type { LinksFunction } from "react-router";
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";

import CommonRender from "./components/CommonRender/CommonRender";
import HeadScriptProvider from "./components/HeadScriptProvider";
import { useArticleSEO } from "./hooks/useArticleSEO";
import { useCustomParams } from "./hooks/useCustomParams";
import useGAData from "./hooks/useGAData";
import { useIsArticlePage } from "./hooks/useIsArticlePage";
import { useRouteResponse } from "./hooks/useRouteResponse";
import { useTheme } from "./hooks/useTheme";
// links
import styles from "./styles/index.css?url";
import { isRealSection } from "./types/Data";
export const links: LinksFunction = () => [{ rel: "stylesheet", href: styles }];

// GTM Tag loaded in CSR to avoid hydration error
function GoogleTagManager({ id }: { id: string }) {
  useEffect(() => {
    if (!id) return;
    (function (w, d, s, i) {
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      const f = d.getElementsByTagName(s)[0];
      const j = d.createElement(s);
      (j as HTMLScriptElement).async = true;
      (j as HTMLScriptElement).src =
        "https://www.googletagmanager.com/gtm.js?id=" + i;
      f.parentNode?.insertBefore(j, f);
    })(window, document, "script", id);
  }, [id]);

  // SSR & CSR 初始渲染都返回 null，不参与 DOM 结构
  return null;
}

// NewRelic config

const NR_ACCOUNT_ID = import.meta.env.VITE_NEW_RELIC_ID || "";
const NR_LICENSE_KEY = import.meta.env.VITE_NEW_RELIC_KEY || "";
const NR_CONFIGURED = !!(NR_ACCOUNT_ID && NR_LICENSE_KEY);

const NR_SNIPPET = !NR_CONFIGURED
  ? ""
  : `
;window.NREUM||(NREUM={});NREUM.init={distributed_tracing:{enabled:true},privacy:{cookies_enabled:true},ajax:{deny_list:["bam.nr-data.net"]}};
;NREUM.loader_config={accountID:"225023",trustKey:"225023",agentID:"${NR_ACCOUNT_ID}",licenseKey:"${NR_LICENSE_KEY}",applicationID:"${NR_ACCOUNT_ID}"};
;NREUM.info={beacon:"bam.nr-data.net",errorBeacon:"bam.nr-data.net",licenseKey:"${NR_LICENSE_KEY}",applicationID:"${NR_ACCOUNT_ID}",sa:1};
`.trim();

console.log("NR from env:", NR_ACCOUNT_ID, NR_LICENSE_KEY, NR_CONFIGURED);

// tmp
// export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
//   if (isRouteErrorResponse(error)) {
//     return (
//       <>
//         <h1>
//           {error.status} {error.statusText}
//         </h1>
//         <p>{error.status}</p>
//       </>
//     );
//   } else if (error instanceof Error) {
//     console.log(error);
//     return (
//       <div>
//         <h1>Error</h1>
//         <p>{error.message}</p>
//         <p>The stack trace is:</p>
//         <pre>{error.stack}</pre>
//       </div>
//     );
//   } else {
//     return <h1>Unknown Error</h1>;
//   }
// }

export default function App(): ReactElement {
  useDevice();

  return (
    <StrictMode>
      <HeadScriptProvider />
      <NeuronProvider>
        <ScrollRestoration />
        <CommonRender>
          <Outlet />
        </CommonRender>
        <span
          id="package-version-tag"
          data-testid="package-version-tag"
          data-version={__APP_VERSION__}
          style={{ display: "none" }}
        >
          {__APP_VERSION__}
        </span>
      </NeuronProvider>
    </StrictMode>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const {
    articleid,
    cue_articleid,
    at,
    author,
    chapter1,
    contentcat,
    contenttype,
    keyword,
    level2,
    pagination,
    page,
    pubdate,
    title,
    visitorcat,
    cd_version,
  } = useGAData();

  const { articleSEOStr = "" } = useArticleSEO();
  const isArticlePage = useIsArticlePage();
  const { article, iasInfo } = useRouteResponse("articlePage");
  const { tagName = "" } = useRouteResponse("keywordPage");

  // theme setup
  const theme = useTheme();

  const { level1 = "" } = useCustomParams();

  const targetings = [
    {
      key: "page",
      value: isArticlePage
        ? "article"
        : level1 == "tags"
          ? "tag"
          : level1 == ""
            ? "home"
            : "listing",
    },
  ] //  lising page
    .concat(
      isRealSection(level1)
        ? [{ key: "page_number", value: page.toString() }]
        : [],
    )
    // keyword page
    .concat(
      level1 == "tags"
        ? [
            { key: "page_number", value: page.toString() },
            { key: "tctags", value: tagName },
          ]
        : [],
    )
    // article page
    .concat(
      isArticlePage
        ? [
            {
              key: "tctags",
              value: article?.tags.map((i) => i.name).join(",") || "",
            },
            { key: "tcarticleid", value: article?.id || "" },
            { key: "gs_channels", value: iasInfo?.gsChannels || "" },
            { key: "fr", value: iasInfo?.fr || "" },
          ]
        : [],
    );

  return (
    <html lang="en" data-theme={theme} className="max-lg:no-scrollbar">
      <head>
        <meta charSet="UTF-8" />
        <link
          href="/favicon-96x96.png"
          rel="icon"
          type="image/png"
          sizes="96x96"
        />
        <link
          href="/favicon-32x32.png"
          rel="icon"
          type="image/png"
          sizes="32x32"
        />
        <link
          href="/favicon-16x16.png"
          rel="icon"
          type="image/png"
          sizes="16x16"
        />
        <link href="/favicon.ico" rel="icon" type="image/x-icon" />
        <link
          href="/apple-touch-icon.png"
          rel="apple-touch-icon"
          sizes="180x180"
        />
        <meta name="apple-mobile-web-app-title" content="ThinkChina" />
        <meta content="width" name="MobileOptimized" />
        <meta content="true" name="HandheldFriendly" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta content="ie=edge" httpEquiv="x-ua-compatible" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,200..1000;1,200..1000&display=swap"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400..700;1,400..700&display=swap"
          rel="stylesheet"
        />

        {/* Article Page SEO */}
        {isArticlePage ? (
          <script dangerouslySetInnerHTML={{ __html: articleSEOStr }} />
        ) : null}
        {/* End Article Page SEO */}

        {/* NewRelic */}
        <script dangerouslySetInnerHTML={{ __html: NR_SNIPPET }} />
        <script
          type="text/javascript"
          src="/scripts/nr-loader-spa.1.286.0.min.js"
          async
        />
        {/* End NewRelic */}

        {/* GA Data */}
        <script
          id="ga_data_layer"
          dangerouslySetInnerHTML={{
            __html: `
            var _data = {
              "articleid":      "${articleid}", 
              "cue_articleid":  "${cue_articleid}",
              "contenttype":    "${contenttype}",
              "at":             "${at}",
              "author":         "${author}",
              "chapter1":       "${chapter1}",
              "contentcat":     "${contentcat}",
              "contenttype":    "${contenttype}",
              "keyword":        "${keyword}",
              "level2":         "${level2}",
              "pagination":     "${pagination}",
              "page":           "${page}",
              "pubdate":        "${pubdate}",
              "title":          "${title}",
              "visitorcat":     "${visitorcat}",
              "cd_version":     "${cd_version}",
            };
          `,
          }}
        />
        {/* End GA Data */}
        {/* <!-- suid, aim to check suid field info  --> */}
        <script src="https://cdn.sphlabs.com/suid/suid.min.js" />

        {/* Google Tag Manager */}
        <GoogleTagManager id="GTM-T7FFJ73" />

        {/* End Google Tag Manager */}
        {/* Google Ads */}
        <script
          id="ads_checker_script"
          type="text/javascript"
          src="https://adtag.sphdigital.com/tag/ads/ads_checker.js"
        />
        <script
          id="google_ads_script"
          async
          type="text/javascript"
          src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"
          crossOrigin="anonymous"
        />
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `
            var googletag = googletag || {};
            googletag.cmd = googletag.cmd || [];
            googletag.cmd.push(function () {
              googletag.setConfig({ disableInitialLoad: true }); // Disable initial load
              googletag.setConfig({ singleRequest: true });
              googletag.setConfig({ collapseDiv: "ON_NO_FILL" });
                            ${targetings
                              .map(
                                (t) =>
                                  `googletag.setConfig({ targeting: { ${JSON.stringify(t.key)}: ${JSON.stringify(t.value)} }});`,
                              )
                              .join("\n")}
              googletag.enableServices();
            });
          `,
          }}
        />
        {/* smx prebid */}
        {import.meta.env.VITE_SMX_PREBID_SCRIPT_URL ? (
          <script
            id="smx_prebid"
            type="text/javascript"
            src={import.meta.env.VITE_SMX_PREBID_SCRIPT_URL}
          />
        ) : null}
        {/* <script
          type="text/javascript"
          src="/scripts/prestitial-postitial-ad-v1.js"
        /> */}
        {/* End Google Ads */}
        <Meta />
        <Links />
      </head>

      <body>
        {/* Prestitial Ad */}
        <div id="prestitial_ad_overlay" />
        <div id="dfp-ad-prestitial" />
        {/* Postitial Ad */}
        <div id="dfp-ad-postitial" />

        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            title="Google Tag Manager"
            src="https://www.googletagmanager.com/ns.html?id=GTM-T7FFJ73"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {/* End Google Tag Manager (noscript) */}

        <div id="app">{children}</div>

        <Scripts />
      </body>
    </html>
  );
}
